export const NodeType = {
  Plain: 0,
  Command: 1,
  Debug: 2,
  Error: 3,
  Info: 4,
  Section: 5,
  Verbose: 6,
  Warning: 7,
  Group: 8,
  EndGroup: 9,
  Icon: 10,
  Notice: 11,
  StartAction: 12, // ##[start-action] marker for composite action regions
  EndAction: 13, // ##[end-action] marker with outcome/conclusion/duration
} as const

export type NodeType = (typeof NodeType)[keyof typeof NodeType]

interface IGroupInfo {
  lineIndex: number
  nodeIndex: number
}

/** Metadata for a composite action region, populated from start-action/end-action log markers. */
export interface IActionInfo {
  /** Unique identifier matching start and end markers */
  id: string
  /** Display name shown on the action bar */
  display: string
  /** Step outcome before continue-on-error (e.g. 'failure' when conclusion is 'success') */
  outcome?: string
  /** Final conclusion after continue-on-error is applied */
  conclusion?: string
  /** How long the action took to execute */
  durationMs?: number
  /** Line index of the start-action marker */
  lineIndex: number
  /** Line index of the end-action marker (undefined while in-progress) */
  endLineIndex?: number
  /** Node index within the ILine */
  nodeIndex: number
  /** Nesting depth (0 = top-level composite, 1 = composite inside composite, etc.) */
  depth: number
}

export interface ISectionFind {
  line: number
}

interface IFindResult {
  lines: ISectionFind[]
  text: string
}

export interface IParsedFindResult extends IFindResult {
  selectedLine: number
}

export interface IParseNode {
  type: NodeType
  /**
   * Index of the node inside ILine
   */
  index: number
  /**
   * Index of the ILine this node belongs to
   */
  lineIndex: number
  /**
   * Starting index of content
   */
  start: number
  /**
   * Ending index of content
   */
  end: number
  /**
   * If this is part of a group, this will refer to the node that's a group
   */
  group?: IGroupInfo
  /**
   * If this is a group, this would be set
   */
  isGroup?: boolean
  /**
   *  The number of hidden lines (endgroup + end-action) before this node
   */
  hiddenLineCount: number
  /**
   * Composite action info if this node is inside an action region
   */
  action?: IActionInfo
  /**
   * True if this node is the start-action marker line
   */
  isActionStart?: boolean
  /**
   * Display name from the start-action marker
   */
  actionDisplay?: string
  /**
   * Action ID from the marker, used to match start/end pairs
   */
  actionId?: string
  /**
   * Line index of the matching end-action marker
   */
  actionEndLineIndex?: number
  /**
   * Line indices of ancestor action start markers (for nested composite actions)
   */
  parentActionLineIndices?: number[]
}

export interface ILine {
  nodes: IParseNode[]
}
